import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import { FiBook, FiUsers, FiTool, FiCalendar, FiArrowRight, FiCheck, FiHeart, FiAward } from 'react-icons/fi';
import colors from '../styles/colors';
import usePageMeta from '../hooks/usePageMeta';

const shine = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const PageWrapper = styled.div`
  padding-top: 80px;
`;

const HeroSection = styled.section`
  padding: 100px 20px;
  background: linear-gradient(135deg, rgba(10, 10, 10, 0.9) 0%, rgba(20, 20, 20, 0.95) 100%),
    url('/images/tools-wall.jpg') center/cover no-repeat;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, ${colors.accent.main}, ${colors.accent.dark});
  }
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: rgba(0, 102, 177, 0.2);
  border: 1px solid rgba(0, 102, 177, 0.4);
  border-radius: 50px;
  color: ${colors.accent.light};
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 20px;
`;

const Title = styled.h1`
  font-size: clamp(2.5rem, 6vw, 4rem);
  color: ${colors.text.heading};
  margin-bottom: 20px;

  span {
    color: ${colors.accent.main};
  }
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  color: ${colors.text.light};
  line-height: 1.8;
  max-width: 650px;
  margin: 0 auto;
`;

const Section = styled.section`
  padding: 100px 20px;
  background: ${props => props.$dark ? colors.background.dark : colors.background.medium};
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const SectionTitle = styled.h2`
  font-size: clamp(2rem, 5vw, 2.8rem);
  color: ${colors.text.heading};
  margin-bottom: 15px;
`;

const SectionSubtitle = styled.p`
  font-size: 1.1rem;
  color: ${colors.text.light};
  max-width: 600px;
  margin: 0 auto;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 50px;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const InfoBox = styled.div`
  background: ${colors.background.card};
  border: 1px solid ${colors.border};
  border-radius: 16px;
  padding: 40px;
`;

const InfoTitle = styled.h3`
  font-size: 1.5rem;
  color: ${colors.text.heading};
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 12px;

  svg {
    color: ${colors.accent.main};
  }
`;

const InfoDesc = styled.p`
  color: ${colors.text.light};
  line-height: 1.8;
  margin-bottom: 25px;
`;

const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  color: ${colors.text.light};
  font-size: 1rem;

  svg {
    color: ${colors.accent.main};
    font-size: 1.1rem;
    flex-shrink: 0;
  }
`;

const ClassCards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ClassCard = styled.div`
  background: ${colors.background.card};
  border: 1px solid ${colors.border};
  border-radius: 12px;
  padding: 25px;
  display: flex;
  align-items: center;
  gap: 20px;
  transition: all 0.3s ease;

  &:hover {
    border-color: ${colors.accent.main};
    transform: translateX(5px);
  }

  @media (max-width: 500px) {
    flex-direction: column;
    text-align: center;
  }
`;

const ClassIcon = styled.div`
  width: 60px;
  height: 60px;
  background: rgba(0, 102, 177, 0.15);
  border: 1px solid rgba(0, 102, 177, 0.3);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.accent.main};
  font-size: 1.5rem;
  flex-shrink: 0;
`;

const ClassInfo = styled.div`
  flex: 1;

  h4 {
    color: ${colors.text.heading};
    font-size: 1.1rem;
    margin-bottom: 6px;
  }

  p {
    color: ${colors.text.muted};
    font-size: 0.9rem;
  }
`;

const ClassTag = styled.span`
  padding: 6px 14px;
  background: linear-gradient(135deg, ${colors.accent.main} 0%, ${colors.accent.dark} 100%);
  color: white;
  font-size: 0.8rem;
  font-weight: 600;
  border-radius: 50px;
  white-space: nowrap;
`;

const DonateSection = styled.section`
  padding: 80px 20px;
  background: ${colors.background.card};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent, rgba(0, 102, 177, 0.05), transparent);
    background-size: 200% 100%;
    animation: ${shine} 4s linear infinite;
  }
`;

const DonateContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
  position: relative;
  z-index: 1;
`;

const DonateIcon = styled.div`
  width: 80px;
  height: 80px;
  background: rgba(0, 102, 177, 0.15);
  border: 2px solid rgba(0, 102, 177, 0.4);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 25px;

  svg {
    font-size: 2.5rem;
    color: ${colors.accent.main};
  }
`;

const DonateTitle = styled.h2`
  font-size: 2rem;
  color: ${colors.text.heading};
  margin-bottom: 15px;
`;

const DonateText = styled.p`
  font-size: 1.1rem;
  color: ${colors.text.light};
  line-height: 1.8;
  margin-bottom: 30px;
`;

const CTAButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 18px 40px;
  background: linear-gradient(135deg, ${colors.accent.main} 0%, ${colors.accent.dark} 100%);
  color: white;
  font-family: 'Oswald', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  border-radius: 6px;
  border: 1px solid ${colors.accent.light};
  box-shadow: 0 4px 20px rgba(0, 102, 177, 0.3);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 30px rgba(0, 102, 177, 0.4);
  }

  svg {
    transition: transform 0.3s ease;
  }

  &:hover svg {
    transform: translateX(5px);
  }
`;

export default function AcademyPage() {
  usePageMeta(
    'HM Academy | Auto Repair Classes in Charlotte, VT | The Honest Mechanic',
    'Learn basic car maintenance and repair at HM Academy in Charlotte, VT. Hands-on classes for youth (ages 11-14) and adults. Tire changes, fluid checks, safety skills & more.'
  );

  return (
    <PageWrapper>
      <HeroSection>
        <HeroContent>
          <Badge><FiBook /> Community Education</Badge>
          <Title>
            HM <span>Academy</span>
          </Title>
          <Subtitle>
            We're passionate about sharing our knowledge. Whether you're a curious middle schooler
            or an adult who wants to understand what's under the hood, our hands-on classes
            will give you the confidence to tackle basic automotive care.
          </Subtitle>
        </HeroContent>
      </HeroSection>

      <Section>
        <Container>
          <Grid>
            <InfoBox>
              <InfoTitle><FiTool /> What You'll Learn</InfoTitle>
              <InfoDesc>
                Our classes cover practical skills you can use right away. No prior experience
                needed — just bring your curiosity and willingness to get your hands a little dirty.
              </InfoDesc>
              <FeatureList>
                <FeatureItem><FiCheck /> Basic vehicle maintenance & fluid checks</FeatureItem>
                <FeatureItem><FiCheck /> How to change a tire safely</FeatureItem>
                <FeatureItem><FiCheck /> Understanding dashboard warning lights</FeatureItem>
                <FeatureItem><FiCheck /> Jump-starting a battery</FeatureItem>
                <FeatureItem><FiCheck /> When to DIY vs. call a professional</FeatureItem>
                <FeatureItem><FiCheck /> Basic tool knowledge & safety</FeatureItem>
              </FeatureList>
            </InfoBox>

            <ClassCards>
              <ClassCard>
                <ClassIcon><FiUsers /></ClassIcon>
                <ClassInfo>
                  <h4>Youth Program</h4>
                  <p>Ages 11-14 • Saturdays • 2 hour sessions</p>
                </ClassInfo>
                <ClassTag>Enrolling</ClassTag>
              </ClassCard>
              <ClassCard>
                <ClassIcon><FiUsers /></ClassIcon>
                <ClassInfo>
                  <h4>Adult Basics</h4>
                  <p>Adults • Weekday evenings • 2 hour sessions</p>
                </ClassInfo>
                <ClassTag>Enrolling</ClassTag>
              </ClassCard>
              <ClassCard>
                <ClassIcon><FiCalendar /></ClassIcon>
                <ClassInfo>
                  <h4>Private Sessions</h4>
                  <p>One-on-one instruction • Flexible scheduling</p>
                </ClassInfo>
                <ClassTag>Available</ClassTag>
              </ClassCard>
            </ClassCards>
          </Grid>
        </Container>
      </Section>

      <Section $dark>
        <Container>
          <SectionHeader>
            <SectionTitle>Why Learn With Us?</SectionTitle>
            <SectionSubtitle>
              Knowledge is power. Understanding your vehicle saves you money and keeps you safe.
            </SectionSubtitle>
          </SectionHeader>
          <Grid>
            <InfoBox>
              <InfoTitle><FiAward /> Hands-On Learning</InfoTitle>
              <InfoDesc>
                We don't just talk about cars — you'll be working on them. Our classes are designed
                to be practical and engaging, with real vehicles and real scenarios.
              </InfoDesc>
            </InfoBox>
            <InfoBox>
              <InfoTitle><FiUsers /> Small Class Sizes</InfoTitle>
              <InfoDesc>
                We keep our classes small so everyone gets individual attention. You'll leave
                with real skills, not just information overload.
              </InfoDesc>
            </InfoBox>
          </Grid>
        </Container>
      </Section>

      <DonateSection>
        <DonateContent>
          <DonateIcon><FiHeart /></DonateIcon>
          <DonateTitle>Donate a Vehicle</DonateTitle>
          <DonateText>
            Have a car you no longer need? Donated vehicles are repaired by students as part of
            their learning experience, then given affordably to community members in need.
            Your donation helps our students learn and helps neighbors get back on the road.
          </DonateText>
          <CTAButton to="/contact">
            Contact Us to Donate <FiArrowRight />
          </CTAButton>
        </DonateContent>
      </DonateSection>
    </PageWrapper>
  );
}
